import React, { FunctionComponent, SyntheticEvent, useState } from 'react';
import { ApolloError } from 'apollo-boost';
import { Card, Grid, Icon } from 'semantic-ui-react';
import { map, pathOr, propEq, filter } from 'ramda';
import InfiniteScroll from 'react-infinite-scroller';
import withStyles from 'react-jss';
import { Link, withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { Location } from 'history';
import queryString from 'query-string';

import Query from 'components/Query';
import { MerchantCardPlaceholder } from 'components/Placeholder';
import { ClassProp } from 'helpers/types';
import FormSelect from 'components/FormSelect';
import { Theme } from 'config';

import MerchantCard from '../MerchantCard/';
import { GET_MERCHANTS } from './MerchantLoader.queries';
import { Merchant } from '../MerchantCard/MerchantCard.helper';

type Pagination = {
  nextOffset: number;
  pageSize: number;
  hasNextPage: number;
};

type Merchants = {
  merchantList: {
    result: Merchant[];
    pagination: Pagination;
  };
};

type QueryResponse = {
  merchantList: Merchants;
  loading?: boolean;
  error?: ApolloError;
};

enum ViewOptions {
  LIVE = 'Live',
  COMPLETE = 'Complete',
  ALL = 'All',
  DOSH_LOCAL = 'Dosh Local',
  ENTERPRISE = 'Enterprise',
}

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  location: Location;
};

type QueryString = {
  searchType?: string | undefined;
  searchText?: string | undefined;
};

export const MERCHANTS_LIMIT = 21;

const styles = (theme: Theme) => ({
  header: {
    margin: '0 !important',
  },
  createMerchant: {
    float: 'left !important',
  },
  formSelect: {
    float: 'right !important',
  },
  noPadding: {
    padding: '1.5rem 0 !important',
    margin: '0 1rem !important',
    display: 'flex',
    justifyContent: 'space-between !important',
    borderBottom: '1px solid #e2e0e0',
  },
  button: {
    display: 'inline-block',
    backgroundColor: `${theme.violet} !important`,
    borderRadius: '1.5rem',
    color: '#fff',
    fontSize: '.8rem',
    marginTop: '0',
    opacity: 1,
    padding: '0.5rem 1.25rem',
    fontWeight: '500',
    lineHeight: '1.25rem',
    cursor: 'pointer',
    textAlign: 'center',
    width: '100%',
    outline: 0,
    border: 'none !important',
    '&:hover': {
      boxShadow:
        '0 0 0 .0625rem transparent inset,0 0 0 0 rgba(34,36,38,.15) inset !important',
      opacity: 0.9,
      color: '#fff',
    },
    '&:active': {
      opacity: 0.7,
    },
  },
  noMatchesFound: {
    marginLeft: '1rem',
    marginRight: '1rem',
    justifyContent: 'center',
  },
  noMatchesFoundContainer: {
    display: 'flex',
    marginTop: '2rem !important',
  },
  noMatchesFoundClearFilter: {
    left: '1rem',
    position: 'relative',
    fontSize: '1rem',
  },
});

const filterOptions = [
  {
    key: ViewOptions.LIVE,
    text: ViewOptions.LIVE,
    value: ViewOptions.LIVE,
  },
  {
    key: ViewOptions.COMPLETE,
    text: ViewOptions.COMPLETE,
    value: ViewOptions.COMPLETE,
  },
  {
    key: ViewOptions.ALL,
    text: ViewOptions.ALL,
    value: ViewOptions.ALL,
  },
  {
    key: ViewOptions.DOSH_LOCAL,
    text: ViewOptions.DOSH_LOCAL,
    value: ViewOptions.DOSH_LOCAL,
  },
  {
    key: ViewOptions.ENTERPRISE,
    text: ViewOptions.ENTERPRISE,
    value: ViewOptions.ENTERPRISE,
  },
];

const filterMerchants = (viewFilter: ViewOptions, merchants: Merchant[]) => {
  switch (viewFilter) {
    case ViewOptions.LIVE:
      return filter(propEq('activeCampaign', true), merchants);
    case ViewOptions.COMPLETE:
      return filter(propEq('activeCampaign', false), merchants);
    case ViewOptions.DOSH_LOCAL:
      return filter(propEq('hasAccountDetails', true), merchants);
    case ViewOptions.ENTERPRISE:
      return filter(propEq('hasAccountDetails', false), merchants);
    default:
      return merchants;
  }
};

const Loader = (
  <Card.Group key={0}>
    <MerchantCardPlaceholder />
    <MerchantCardPlaceholder />
    <MerchantCardPlaceholder />
  </Card.Group>
);

const renderMerchantCard = (merchant: Merchant) => (
  <MerchantCard
    key={merchant.offerNetworkExternalVenueId}
    merchant={merchant}
  />
);

const formatSearchType = (name: string) =>
  name.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());

const renderCards = (
  classes: Styles,
  search: Record<string, string>,
  [viewFilter, setViewFilter]: [
    ViewOptions,
    React.Dispatch<React.SetStateAction<ViewOptions>>,
  ],
) => (
  response: QueryResponse,
  loadMore: (offset: number, limit: number) => () => void,
) => {
  const onFilterChange = (
    _: SyntheticEvent<HTMLElement, Event>,
    { value }: { value: ViewOptions },
  ) => {
    setViewFilter(value);
  };

  const merchantList = pathOr(
    [],
    ['data', 'merchantList', 'result'],
    response,
  ) as Merchant[];

  const { hasNextPage, nextOffset, pageSize } = pathOr(
    {},
    ['data', 'merchantList', 'pagination'],
    response,
  );

  const loadMoreFunc = () => {
    if (!response.loading && hasNextPage && loadMore) {
      loadMore(nextOffset, pageSize);
    }
  };

  const resetFilter = () => {
    setViewFilter(ViewOptions.ALL);
  };

  const appliedSearchType = Object.keys(search)[0];
  const hasAppliedSearch = !!appliedSearchType;

  const hasMore = hasNextPage === undefined ? true : hasNextPage;

  const filteredMerchants = filterMerchants(viewFilter, merchantList);

  return (
    <>
      <Grid className={classes.header}>
        <Grid.Row className={classes.noPadding}>
          <div className={classes.createMerchant}>
            <a href="/create-merchant" className={classes.button}>
              Create Merchant
            </a>
          </div>

          <div className={classes.formSelect}>
            <FormSelect
              name="filter"
              value={viewFilter}
              placeholder="View"
              label="Filter Options: "
              options={filterOptions}
              onChange={onFilterChange}
            />
          </div>
        </Grid.Row>
        {hasAppliedSearch && (
          <Grid.Row className={classes.noPadding}>
            <h3>
              Results for {formatSearchType(appliedSearchType)} - &quot;
              {search[appliedSearchType]}&quot;
            </h3>
            <Link to="/">
              <Icon name="arrow alternate circle left outline" />
              Back to All Merchants
            </Link>
          </Grid.Row>
        )}
      </Grid>

      <InfiniteScroll
        loadMore={loadMoreFunc}
        hasMore={hasMore}
        loader={Loader}
        pageStart={0}
        initialLoad={false}
      >
        {filteredMerchants.length === 0 && viewFilter !== ViewOptions.ALL && (
          <div className={classes.noMatchesFoundContainer}>
            <h3 className={classes.noMatchesFound}>
              No merchants found matching selected filter
              <span className={classes.noMatchesFoundClearFilter}>
                <a href="void(0)" onClick={resetFilter}>
                  Clear filter
                </a>
              </span>
            </h3>
          </div>
        )}
        <Card.Group centered>
          {map(renderMerchantCard, filteredMerchants)}
        </Card.Group>
      </InfiniteScroll>
    </>
  );
};

const MerchantLoader: FunctionComponent<Props> = ({ classes, location }) => {
  const filterState = useState<ViewOptions>(ViewOptions.ALL);

  const { searchType = '', searchText = '' }: QueryString = queryString.parse(
    location.search,
  );

  const search = searchType ? { [searchType]: searchText } : {};

  return (
    <Query
      notifyOnNetworkStatusChange
      enableProgressInfo
      query={GET_MERCHANTS}
      render={renderCards(classes, search, filterState)}
      variables={{
        params: {
          pagingInput: {
            offset: 0,
            limit: MERCHANTS_LIMIT,
          },
          ...search,
        },
        package: 'DEFAULT_BRANDING',
      }}
    />
  );
};

export default compose<Props, any>(
  withRouter,
  withStyles(styles),
)(MerchantLoader);
